import { isEmpty, set, unset } from 'lodash'
import consola from 'consola'
import config from '@/cms.config'

const logger = consola.withTag('EditableDirective')

const editorModes = Object.assign({}, {
  default: ['bold', 'italic', 'underline', 'link', 'unlink', 'h2', 'h3', 'blockquote'],
  text: false,
  html: false
}, config.editorModes)

if (process.browser) {
// Import TinyMCE
  window.tinymce = require('tinymce/tinymce')
// Default icons are required for TinyMCE 5.3 or above
  require('tinymce/icons/default')
// A theme is also required
  require('tinymce/themes/silver')
// Any plugins you want to use has to be imported
  require('tinymce/plugins/quickbars')
  require('tinymce/plugins/link')
  require('tinymce/plugins/lists')
  require('../inline-editor/plugins/page-browser')
// Language packs
  require('../inline-editor/langs/de')
// Load skin
  require('tinymce/skins/ui/oxide/skin.min.css')
  require('tinymce/skins/ui/oxide/content.inline.min.css')
}

const installEditable = (el, { expression, value, modifiers }, vnode) => {
  const mods = modifiers && !isEmpty(modifiers) ? Object.keys(modifiers) : []
  const isSingleLine = !isEmpty(mods) && mods.includes('oneLine')
  const emitsEvents = !isEmpty(mods) && mods.includes('emit')
  const resource = vnode.context.resource
  const cmsEnabled = vnode.context.$cms && vnode.context.$cms.editable
  const path = expression
  const relativePath = path ? path.split('.').slice(1).join('.') : null
  const initialValue = value.replace(/<\/a>\s?$/, '</a><br/>');
  const fieldClassName = 'cms-field'
  const focusedClassName = `${fieldClassName}--focused`
  const touchedClassName = `${fieldClassName}--touched`
  const editedClassName = `${fieldClassName}--edited`
  const htmlEnabledClassName = `${fieldClassName}--html-enabled`

  // Determine mode
  const baseMod = mods.filter((mod) => !['oneLine', 'emit'].includes(mod)).pop()
  const mode = baseMod && editorModes[baseMod] !== undefined ? baseMod : 'default'
  const isText = mode === 'text'
  const isHTML = mode === 'html'
  const innerValue = isText ? 'innerText' : 'innerHTML'

  let changed = false

  const placeholder = vnode.data.directives.find(d => d.name === 'placeholder')

  // Use a custom class to adjust editor styles
  const stylesClassNames = ['editable']
  // Add mods to classes, helpful for tests
  mods.forEach(mod => stylesClassNames.push(`editable--${mod}`))

  if (placeholder) {
    if (placeholder.value === 'single') {
      el.setAttribute('data-placeholder', 'Lorem ipsum dolor sit amet')
    } else if (placeholder.value === 'multi') {
      el.setAttribute('data-placeholder', 'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.')
    } else {
      el.setAttribute('data-placeholder', placeholder.value)
    }
  } else {
    el.setAttribute('data-placeholder', 'Lorem ipsum dolor sit amet')
  }

  const registered = vnode.context.inlineEditable.find(i => i === expression)
  if (!registered) {
    logger.debug('Registering as inline editable:', expression)
    vnode.context.inlineEditable.push(expression)
  }

  vnode.context.editedFields = {}
  set(vnode.context.editedFields, relativePath, initialValue)

  el[innerValue] = initialValue
  // add classes to editable element
  el.classList.add(...stylesClassNames)

  if (!cmsEnabled || (resource && resource.locked)) {
    el.contentEditable = false
    return
  }

  el.classList.add(fieldClassName)

  if (!isText) {
    el.classList.add(htmlEnabledClassName)
  }

  // define events handlers
  const handleClick = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleFocus = () => {
    el.classList.add(focusedClassName)
    el.classList.add(touchedClassName)
  }

  const handleInput = () => {
    el.classList.add(touchedClassName)

    changed = true

    if (el[innerValue] !== initialValue) {
      set(vnode.context.editedFields, relativePath, el[innerValue])
      el.classList.add(editedClassName)
    } else {
      unset(vnode.context.editedFields, relativePath)
      el.classList.remove(editedClassName)
    }

    if (emitsEvents) {
      vnode.context.$emit('editable:input', {
        path,
        value: el[innerValue]
      });
    } else if (typeof vnode.context.handleEditableInput === 'function') {
      vnode.context.handleEditableInput({
        path: relativePath,
        value: el[innerValue]
      })
    }
  }

  const handleBlur = () => {
    el.classList.remove(focusedClassName)

    if (!changed) return;

    if (emitsEvents) {
      vnode.context.$emit('editable:blur', {
        path,
        value: el[innerValue]
      })
    } else if (changed && typeof vnode.context.handleEditableBlur === 'function') {
      vnode.context.handleEditableBlur({
        path: relativePath,
        value: el[innerValue]
      })
    }

    changed = false
  }

  if (process.browser && !isHTML) {
    // Add project specific requirements
    const editorExist = !!window.tinymce.get(el.id)
    if (!editorExist) {
      config.beforeEditorInit && config.beforeEditorInit();
      window.tinymce.init({
        // overridable configs
        language: 'de',
        link_class_list: [
          { title: 'Link', value: 'link' }
        ],
        default_link_target: '_blank',

        // user configs
        ...config.editorConfig,

        // required configs
        target: el,
        inline: true,
        skin: false,
        plugins: [
          'quickbars',
          'link',
          'lists',
          'pageBrowser',
          ...(config.editorConfig && Array.isArray(config.editorConfig.plugins)
              ? config.editorConfig.plugins
              : []
          )
        ],
        menubar: false,
        toolbar: false,
        contextmenu: false,
        quickbars_insert_toolbar: false,
        quickbars_selection_toolbar: isText ? false : editorModes[mode].join(' '),
        setup (editor) {
          if (isSingleLine) {
            editor.on('keydown', (event) => {
              if (event.keyCode === 13) {
                event.preventDefault()
                event.stopPropagation()
                return false
              }
            })
          }
          editor.on('blur', () => handleBlur())
          editor.on('focus', () => handleFocus())
          editor.on('Change input', () => handleInput())

          if (config.editorConfig && typeof config.editorConfig.setup === 'function') {
            config.editorConfig.setup(editor)
          }
        }
      })
    }
  } else {
    el.contentEditable = true
    el.onfocus = handleFocus
    el.oninput = handleInput
    el.onblur = handleBlur
  }
  el.addEventListener('click', handleClick)
}

export default {
  bind: installEditable,
  componentUpdated: installEditable
}
